import React, { useState, useEffect, forwardRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { database } from '../supabase';
import { getAnalyticsSummary, getAnalyticsData } from '../utils/analytics';
import LayoutWithSidebars from '../components/LayoutWithSidebars';
import './StudyPlan.css';
import { useNavigate } from 'react-router-dom';
import { Menu, TrendingUp } from 'lucide-react';

const StudyPlan = forwardRef(({ toggleSidebar }, ref) => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [analyticsData, setAnalyticsData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);



    const toggleFlashcards = () => {
        console.log('Flashcards not available in StudyPlan');
    };

    const toggleQuiz = () => {
        console.log('Quiz not available in StudyPlan');
    };

    const toggleChat = () => {
        console.log('Chat not available in StudyPlan');
    };


    useEffect(() => {
        if (window.location.pathname === '/study-plan') {
            const featuresElem = document.querySelector('.flash-cards-icon-row');
            if (featuresElem) {
                featuresElem.style.display = 'none';
            }
        }
        // Optional: cleanup to restore display if needed
        return () => {
            const featuresElem = document.querySelector('.flash-cards-icon-row');
            if (featuresElem) {
                featuresElem.style.display = '';
            }
        };
    }, []);



    useEffect(() => {
        const fetchAnalytics = async () => {
            if (!user) return;
            
            try {
                setLoading(true);
                console.log('📊 Fetching analytics for Study Plan page');
                
                // Get Supabase user ID
                const supabaseUser = await database.getUserByFirebaseUid(user.uid);
                if (!supabaseUser) {
                    throw new Error('User not found in Supabase');
                }

                // Get analytics summary
                const summary = await getAnalyticsSummary(supabaseUser.id);
                const fullData = await getAnalyticsData(supabaseUser.id);
                
                setAnalyticsData({
                    summary,
                    fullData
                });
                
                console.log('✅ Analytics loaded for Study Plan:', summary);
            } catch (err) {
                console.error('❌ Error fetching analytics:', err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchAnalytics();
    }, [user]);

    if (loading) {
        return (
            <LayoutWithSidebars
                documents={[]}
                toggleFlashcards={toggleFlashcards}
                toggleQuiz={toggleQuiz}
                toggleChat={toggleChat}
            >
                <div className="study-plan-content">
                    <div className="loading-container">
                        <div className="loading-spinner"></div>
                        <p>Loading your study analytics...</p>
                    </div>
                </div>
            </LayoutWithSidebars>
        );
    }

    if (error) {
        return (
            <LayoutWithSidebars
                documents={[]}
                toggleFlashcards={toggleFlashcards}
                toggleQuiz={toggleQuiz}
                toggleChat={toggleChat}
            >
                <div className="study-plan-content">
                    <div className="error-container">
                        <h2>Unable to load analytics</h2>
                        <p>{error}</p>
                    </div>
                </div>
            </LayoutWithSidebars>
        );
    }

    if (!analyticsData) {
        return (
            <LayoutWithSidebars
                documents={[]}
                toggleFlashcards={toggleFlashcards}
                toggleQuiz={toggleQuiz}
                toggleChat={toggleChat}
            >
                <div className="study-plan-content">
                    <div className="no-data-container">
                        <div className="trophy-icon">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M12 2L13.09 8.26L20 9L13.09 9.74L12 16L10.91 9.74L4 9L10.91 8.26L12 2Z" fill="#4ECDC4"/>
                                <path d="M7 14L8.5 17L12 15.5L15.5 17L17 14L14.5 12L17 10L15.5 7L12 8.5L8.5 7L7 10L9.5 12L7 14Z" fill="#4ECDC4"/>
                            </svg>
                        </div>
                        <h2>Start Your Learning Journey</h2>
                        <p>Take some quizzes and study flashcards to see your analytics here!</p>
                        <div className="cta-buttons">
                            <button className="cta-button primary" onClick={() => window.location.href = `/main/${user.uid}`}>
                                Go to Dashboard →
                            </button>
                        </div>
                    </div>
                </div>
            </LayoutWithSidebars>
        );
    }

    const { summary, fullData } = analyticsData;


    // Check if we have meaningful data
    const hasData = summary.totalQuizAttempts > 0 || summary.totalFlashcards > 0;

    return (
        <div className="study-plan-content">
                {/* Mobile Menu Header */}
                <div className="study-plan-header">
                    <div className="header-left">
                        <h1 className="study-plan-title">Study Plan</h1>
                        <div className="header-left-mobile">
                            <Menu onClick={toggleSidebar} className="header-left-mobile-icon" />
                        </div>
                    </div>
                </div>
                
                <div className="content-wrapper">

                <div className={`analytics-section ${!hasData ? 'blurred-section' : ''}`}>
                    <h3 className="section-title">Our AI Study Recommendations</h3>
                    <p className="section-subtitle">Personalized insights just for you to boost your performance <br /> and help you achieve your goals!</p>
                    
                    {!hasData ? (
                        // Show blurred placeholder content when no data
                        <div className="blurred-content">
                            <div className="recommendations-list">
                                <div className="recommendation-card">
                                    <div className="recommendation-header">
                                        <span className="recommendation-priority high">HIGH</span>
                                        <h4 className="recommendation-title">Overall Study Strategy Needs Improvement</h4>
                                    </div>
                                    <p className="recommendation-description">
                                        Your overall quiz performance of 0.0% indicates significant gaps in your understanding. You've answered undefined out of undefined questions correctly across all topics. This performance level suggests you need to focus on building a solid foundation before attempting more advanced concepts...
                                    </p>
                                    <div className="recommendation-action">
                                        <span className="action-text">Revise study approach and focus on understanding before testing</span>
                                    </div>
                                </div>
                            </div>
                            
                            {/* Priority Legend */}
                            <div className="priority-legend">
                                <h4 className="legend-title">Priority Levels</h4>
                                <div className="legend-items">
                                    <div className="legend-item">
                                        <span className="legend-priority high">HIGH</span>
                                        <span className="legend-description">Critical areas that need immediate attention</span>
                                    </div>
                                    <div className="legend-item">
                                        <span className="legend-priority medium">MEDIUM</span>
                                        <span className="legend-description">Important areas for improvement</span>
                                    </div>
                                    <div className="legend-item">
                                        <span className="legend-priority low">LOW</span>
                                        <span className="legend-description">Positive reinforcement and advanced topics</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : fullData.recommendations.length > 0 ? (
                        // Show real recommendations when data exists
                        <>
                            <div className="recommendations-list">
                                {fullData.recommendations.slice(0, 3).map((rec, index) => (
                                    <div key={index} className="recommendation-card">
                                        <div className="recommendation-header">
                                            <span className={`recommendation-priority ${rec.priority}`}>
                                                {rec.priority.toUpperCase()}
                                            </span>
                                            <h4 className="recommendation-title">{rec.title}</h4>
                                        </div>
                                        <p className="recommendation-description">
                                            {rec.description.length > 200 
                                                ? (
                                                    <>
                                                        <span className="short-text">
                                                            {rec.description.substring(0, 200)}...
                                                            <button 
                                                                className="read-more-btn"
                                                                onClick={(e) => {
                                                                    e.target.parentElement.style.display = 'none';
                                                                    e.target.parentElement.nextSibling.style.display = 'block';
                                                                }}
                                                            >
                                                                Read more
                                                            </button>
                                                        </span>
                                                        <span className="full-text" style={{display: 'none'}}>
                                                            {rec.description}
                                                            <button 
                                                                className="read-less-btn"
                                                                onClick={(e) => {
                                                                    e.target.parentElement.style.display = 'none';
                                                                    e.target.parentElement.previousSibling.style.display = 'block';
                                                                }}
                                                            >
                                                                Read less
                                                            </button>
                                                        </span>
                                                    </>
                                                )
                                                : rec.description
                                            }
                                        </p>
                                        <div className="recommendation-action">
                                            <span className="action-text">{rec.action}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            
                            {/* Priority Legend */}
                            <div className="priority-legend">
                                <h4 className="legend-title">Priority Levels</h4>
                                <div className="legend-items">
                                    <div className="legend-item">
                                        <span className="legend-priority high">HIGH</span>
                                        <span className="legend-description">Critical areas that need immediate attention</span>
                                    </div>
                                    <div className="legend-item">
                                        <span className="legend-priority medium">MEDIUM</span>
                                        <span className="legend-description">Important areas for improvement</span>
                                    </div>
                                    <div className="legend-item">
                                        <span className="legend-priority low">LOW</span>
                                        <span className="legend-description">Positive reinforcement and advanced topics</span>
                                    </div>
                                </div>
                            </div>
                        </>
                    ) : null}
                    
                    {/* Overlay with CTA when no data */}
                    {!hasData && (
                        <div className="blur-overlay">
                            <div className="overlay-content">
                                <h3>Generate docs to begin</h3>
                                <p>Upload your first document to unlock personalized study recommendations</p>
                                <button 
                                    className="generate-docs-btn"
                                    onClick={() => navigate(`/main/${user.uid}`)}
                                >
                                    Upload Documents →
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                        {/* Study Time & Progress Section */}
                        <div className="analytics-section">
                            <h3 className="section-title">Study Progress</h3>
                            <p className="section-subtitle">Your learning journey overview</p>
                            
                            <div className="progress-stats">
                                <div className="stat-item">
                                    <div className="stat-value">{summary.totalQuizAttempts || 0}</div>
                                    <div className="stat-label">Quizzes Completed</div>
                                </div>
                                <div className="stat-item">
                                    <div className="stat-value">{summary.totalFlashcards || 0}</div>
                                    <div className="stat-label">Flashcards Studied</div>
                                </div>
                                <div className="stat-item">
                                    <div className="stat-value">{summary.studyTime?.formatted || '0 min'}</div>
                                    <div className="stat-label">Total Study Time</div>
                                </div>
                                <div className="stat-item">
                                    <div className="stat-value">{summary.documentsCount || 0}</div>
                                    <div className="stat-label">Documents Studied</div>
                                </div>
                            </div>
                        </div>
                    {/* Performance Overview Card */}
                    <div className="performance-overview-card">
                    <div className="performance-metrics">
                        <div className="metric-row">
                            <span className="metric-label">Quiz Performance</span>
                            <div className="progress-container">
                                <div className="studyplan-progress-bar">
                                    <div 
                                        className="progress-fill quiz-progress" 
                                        style={{ width: `${hasData ? summary.averageQuizScore : 0}%` }}
                                    ></div>
                                </div>
                                <span className="progress-percentage quiz-color">
                                    {hasData ? summary.averageQuizScore.toFixed(0) : 0}%
                                </span>
                            </div>
                        </div>
                        
                        <div className="metric-row">
                            <span className="metric-label">Flashcard Mastery</span>
                            <div className="progress-container">
                                <div className="studyplan-progress-bar">
                                    <div 
                                        className="progress-fill flashcard-progress" 
                                        style={{ width: `${hasData ? summary.flashcardMastery : 0}%` }}
                                    ></div>
                                </div>
                                <span className="progress-percentage flashcard-color">
                                    {hasData ? summary.flashcardMastery.toFixed(0) : 0}%
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Main Call-to-Action Card */}
                {/* <div className="cta-card">
                    <div className="cta-content">
                        <div className="trophy-icon">
                           <TrendingUp className='trophy-icon-icon' />
                        </div>
                        
                        <h2 className="cta-title">Ready to master your studies?</h2>
                        
                        <p className="cta-subtitle">
                            {!hasData 
                                ? "Start your learning journey by taking quizzes and studying flashcards to build your analytics!"
                                : summary.gpa >= 3.5 
                                ? `You're excelling with a ${summary.gpaGrade} GPA! Let's tackle your next challenge.`
                                : summary.gpa >= 3.0
                                ? `Great progress with a ${summary.gpaGrade} GPA! Let's push for even higher scores.`
                                : `You're building momentum! Let's focus on improving your ${summary.gpaGrade} GPA.`
                            }
                        </p>
                        
                        <div className="cta-buttons">
                            <button 
                                className="cta-button primary"
                                onClick={() => navigate(`/my-library/${user.uid}`)}
                            >
                                Continue Studying →
                            </button>
                            <button 
                                className="cta-button secondary"
                                onClick={() => navigate(`/main/${user.uid}`)}
                            >
                                Take Quizzes
                            </button>
                        </div>
                    </div>
                </div> */}

                {/* Get Started Section - Show when no data */}
                {!hasData && (
                    <div className="analytics-section">
                        <h3 className="section-title">Get Started with Analytics</h3>
                        <p className="section-subtitle">Take quizzes and study flashcards to unlock your learning insights</p>
                        
                        <div className="get-started-steps">
                            <div className="step-card">
                                <div className="step-number">1</div>
                                <div className="step-content">
                                    <h4>Upload a Document</h4>
                                    <p>Upload a PDF or text document to create study materials</p>
                                </div>
                            </div>
                            <div className="step-card">
                                <div className="step-number">2</div>
                                <div className="step-content">
                                    <h4>Generate Quizzes</h4>
                                    <p>Create quizzes from your documents and start taking them</p>
                                </div>
                            </div>
                            <div className="step-card">
                                <div className="step-number">3</div>
                                <div className="step-content">
                                    <h4>Study Flashcards</h4>
                                    <p>Generate and study flashcards to track your progress</p>
                                </div>
                            </div>
                            <div className="step-card">
                                <div className="step-number">4</div>
                                <div className="step-content">
                                    <h4>View Analytics</h4>
                                    <p>Return here to see your GPA, progress, and AI recommendations</p>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Additional Analytics Sections */}
                {fullData && hasData && (
                    <>
                        {/* Weak Topics Section */}
                        {fullData.topicAnalysis.weakTopics.length > 0 && (
                            <div className="analytics-section">
                                <h3 className="section-title">Focus Areas</h3>
                                <p className="section-subtitle">Topics that need your attention</p>
                                
                                <div className="topics-grid">
                                    {fullData.topicAnalysis.weakTopics.slice(0, 3).map((topic, index) => (
                                        <div key={index} className="topic-card weak-topic">
                                            <div className="topic-header">
                                                <span className="topic-name">{topic.topic}</span>
                                                <span className="topic-percentage weak-color">
                                                    {topic.accuracy.toFixed(0)}%
                                                </span>
                                            </div>
                                            <div className="topic-description">
                                                {topic.correctAnswers}/{topic.totalQuestions} questions correct
                                            </div>
                                            <div className="topic-progress-bar">
                                                <div 
                                                    className="topic-progress-fill weak-fill"
                                                    style={{ width: `${topic.accuracy}%` }}
                                                ></div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Strong Topics Section */}
                        {fullData.topicAnalysis.strongTopics.length > 0 && (
                            <div className="analytics-section">
                                <h3 className="section-title">Your Strengths</h3>
                                <p className="section-subtitle">Topics where you excel</p>
                                
                                <div className="topics-grid">
                                    {fullData.topicAnalysis.strongTopics.slice(0, 3).map((topic, index) => (
                                        <div key={index} className="topic-card strong-topic">
                                            <div className="topic-header">
                                                <span className="topic-name">{topic.topic}</span>
                                                <span className="topic-percentage strong-color">
                                                    {topic.accuracy.toFixed(0)}%
                                                </span>
                                            </div>
                                            <div className="topic-description">
                                                {topic.correctAnswers}/{topic.totalQuestions} questions correct
                                            </div>
                                            <div className="topic-progress-bar">
                                                <div 
                                                    className="topic-progress-fill strong-fill"
                                                    style={{ width: `${topic.accuracy}%` }}
                                                ></div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* AI Recommendations Section */}
                   

<div className="cta-card">
                    <div className="cta-content">
                        <div className="trophy-icon">
                           <TrendingUp className='trophy-icon-icon' />
                        </div>
                        
                        <h2 className="cta-title">Ready to master your studies?</h2>
                        
                        <p className="cta-subtitle">
                            {!hasData 
                                ? "Start your learning journey by taking quizzes and studying flashcards to build your analytics!"
                                : summary.gpa >= 3.5 
                                ? `You're excelling with a ${summary.gpaGrade} GPA! Let's tackle your next challenge.`
                                : summary.gpa >= 3.0
                                ? `Great progress with a ${summary.gpaGrade} GPA! Let's push for even higher scores.`
                                : `You're building momentum! Let's focus on improving your ${summary.gpaGrade} GPA.`
                            }
                        </p>
                        
                        <div className="cta-buttons">
                            <button 
                                className="cta-button primary"
                                onClick={() => navigate(`/my-library/${user.uid}`)}
                            >
                                Review Materials →
                            </button>
                            <button 
                                className="cta-button secondary"
                                onClick={() => navigate(`/main/${user.uid}`)}
                            >
                                Take Quizzes
                            </button>
                        </div>
                    </div>
                </div>

                    </>
                )}
                </div>
            </div>
    );
});

export default StudyPlan;
